import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createProduct, fetchMediaLimits, getProduct, updateProductFields } from '../../services/api';

// Capacidades de media: derivadas del Media Capabilities Registry (backend) vía
// /api/upload/limits. El frontend NO define listas de formatos (KF25_SPEC_MEDIA_REGISTRY).
const MAX_FILES = 10;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

interface UploadProps {
  onProductsUpdated?: () => void;
  // UX-001E: con editCode el MISMO formulario opera en Edit Mode (valores
  // precargados, submit por PATCH /api/products/:code). Sin editCode = Create.
  editCode?: string | null;
  // UX-001E (flujo preferido): callback de éxito para uso modal — el contenedor
  // (Product Console) cierra el modal sin navegar. Sin onSuccess (flujo por ruta)
  // se mantiene la pantalla de éxito con "👁️ Ver en consola".
  onSuccess?: () => void;
}

export const UploadConsole: React.FC<UploadProps> = ({ onProductsUpdated, editCode, onSuccess }) => {
  const navigate = useNavigate();
  const isEdit = !!editCode;
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [resultCode, setResultCode] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [activePreview, setActivePreview] = useState(0);
  const [mediaLimits, setMediaLimits] = useState<any>(null);
  // UX-001E: media existente (strip read-only, type-aware) + carga del producto
  const [existingMedia, setExistingMedia] = useState<any[]>([]);
  const [editLoading, setEditLoading] = useState(isEdit);
  const [editLoadError, setEditLoadError] = useState<string | null>(null);
  const previewUrls = useRef<string[]>([]);

  // UX-001E: Edit Mode — precargar TODOS los campos del producto existente
  useEffect(() => {
    if (!editCode) return;
    let cancelled = false;
    getProduct(editCode, { includeHidden: true })
      .then(r => {
        if (cancelled || !r?.success) return;
        const p = r.product;
        setName(p.name || '');
        setPrice(p.price != null ? String(p.price) : '');
        setCategory(p.category || '');
        setDescription(p.description || '');
        // media[] tipado; fallback legacy images[] (misma heurística de UX-001C)
        const media = Array.isArray(p.media) && p.media.length > 0
          ? p.media
          : (p.images || []).map((u: string) => ({ type: /\.(mp4|mov|webm)$/i.test(u) ? 'video' : 'image', url: u }));
        setExistingMedia(media);
      })
      .catch(() => { if (!cancelled) setEditLoadError('No se pudo cargar el producto para editar.'); })
      .finally(() => { if (!cancelled) setEditLoading(false); });
    return () => { cancelled = true; };
  }, [editCode]);

  // Derivar capacidades de media del registro (backend). Fallback = comportamiento actual.
  useEffect(() => {
    fetchMediaLimits()
      .then((data) => data && data.limits && setMediaLimits(data.limits))
      .catch(() => { /* fallback: accept genérico */ });
  }, []);

  // Generate object URLs for previews, cleanup on unmount
  useEffect(() => {
    return () => {
      previewUrls.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const getPreviewUrl = (file: File, index: number): string => {
    if (!previewUrls.current[index]) {
      previewUrls.current[index] = URL.createObjectURL(file);
    }
    return previewUrls.current[index];
  };

  // Reset active preview index when files change
  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    // Check file size
    const oversized = selected.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setError(`El archivo "${oversized.name}" excede 50MB. Redimensiona o comprime antes de subir.`);
      setStatus('error');
      return;
    }

    // Check total files limit
    const total = files.length + selected.length;
    if (total > MAX_FILES) {
      setError(`Máximo ${MAX_FILES} archivos por producto. Ya tienes ${files.length} seleccionado(s).`);
      setStatus('error');
      return;
    }

    setFiles(prev => [...prev, ...selected]);
    setActivePreview(0);
  };

  const removeFile = (index: number) => {
    setFiles(prev => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke the removed URL
      if (previewUrls.current[index]) {
        URL.revokeObjectURL(previewUrls.current[index]);
        previewUrls.current[index] = '';
      }
      // Shift remaining URLs
      previewUrls.current.splice(index, 1);
      return updated;
    });
    if (activePreview >= files.length - 1) {
      setActivePreview(Math.max(0, files.length - 2));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    // Local validation (compartida Create/Edit — misma lógica, sin duplicación)
    if (!isEdit && files.length === 0) {
      setError('Selecciona al menos una foto o video.');
      setStatus('error');
      return;
    }
    if (!name.trim()) {
      setError('El nombre del artículo es obligatorio.');
      setStatus('error');
      return;
    }
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
      setError('Precio inválido. Ingresa un número mayor a 0.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('loading');
    setError(null);

    try {
      if (isEdit && editCode) {
        // UX-001E: Edit Mode → PATCH /api/products/:code (endpoint existente,
        // emite product.changed → realtime). Media intacta (fuera de alcance).
        await updateProductFields(editCode, {
          name: name.trim(),
          price,
          category: category.trim(),
          description: description.trim(),
        });
      } else {
        const formData = new FormData();
        files.forEach(f => formData.append('media', f));
        formData.append('name', name.trim());
        formData.append('price', price);
        if (category.trim()) formData.append('category', category.trim());
        if (description.trim()) formData.append('description', description.trim());

        const result = await createProduct(formData);
        setResultCode(result.code);
      }
      setStatus('success');
      if (onProductsUpdated) onProductsUpdated();
      // UX-001E modal: cerrar sin navegar (el contenedor refresca el producto)
      if (onSuccess) onSuccess();
    } catch (err: any) {
      const msg = err?.message || err?.response?.data?.message || 'Error al guardar el artículo. Intenta de nuevo.';
      setError(msg);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStatus('idle');
    setFiles([]);
    setName('');
    setPrice('');
    setCategory('');
    setDescription('');
    setError(null);
    setResultCode(null);
    setFormKey(k => k + 1); // Recreate DOM → fresh file input
    setActivePreview(0);
    // Revoke all preview URLs
    previewUrls.current.forEach(url => URL.revokeObjectURL(url));
    previewUrls.current = [];
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <button onClick={() => navigate('/admin')}
        className="text-purple-600 hover:text-purple-800 font-medium mb-4 block">
        ← Volver al panel
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-2">{isEdit ? '✏️ Editar producto' : '📤 Subir producto'}</h1>
      <p className="text-gray-600 mb-6">{isEdit ? 'Modifica solo lo necesario y guarda los cambios' : 'Agrega un nuevo artículo a la tienda'}</p>

      {editLoading && (
        <div className="text-center py-12 text-gray-500">Cargando producto para editar...</div>
      )}

      {editLoadError && !editLoading && (
        <div className="text-center py-8 bg-red-50 rounded-xl border-2 border-red-300 mb-6">
          <p className="text-red-700 mb-4">{editLoadError}</p>
          <button onClick={() => navigate('/admin')} className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors">
            Volver al panel
          </button>
        </div>
      )}

      {/* UX-001E: strip read-only de media existente (type-aware — patrón UX-001C).
          La media se gestiona desde la consola del producto (+ Foto / + Video). */}
      {isEdit && !editLoading && !editLoadError && existingMedia.length > 0 && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Media actual <span className="text-gray-400 font-normal">({existingMedia.length} archivo(s) — no editable aquí)</span>
          </label>
          <div className="flex gap-2 overflow-x-auto">
            {existingMedia.map((m, i) =>
              m.type === 'video' ? (
                <video key={i} src={m.url} muted playsInline preload="metadata"
                  className="w-20 h-20 rounded-lg object-contain bg-gray-100 flex-shrink-0 product-media" />
              ) : (
                <img key={i} src={m.url} alt="" className="w-20 h-20 rounded-lg object-contain bg-gray-100 flex-shrink-0 product-media" />
              )
            )}
          </div>
          <p className="text-xs text-gray-400 mt-1">Para agregar o quitar media: consola del producto → + Foto / + Video.</p>
        </div>
      )}

      {/* Preview block: appears when files are selected */}
      {files.length > 0 && status === 'idle' && (
        <div className="mb-6 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
          {/* Main preview */}
          <div className="relative bg-black/5 flex items-center justify-center min-h-[250px] sm:min-h-[320px]">
            {files[activePreview]?.type.startsWith('video/') ? (
              <video
                key={activePreview}
                src={getPreviewUrl(files[activePreview], activePreview)}
                controls
                preload="metadata"
                className="max-h-80 sm:max-h-96 w-full object-contain"
              />
            ) : (
              <img
                key={activePreview}
                src={getPreviewUrl(files[activePreview], activePreview)}
                alt={`Preview ${activePreview + 1}`}
                className="max-h-80 sm:max-h-96 w-full object-contain p-2"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
            )}
            {/* Fallback for HEIC/HEIF or broken images */}
            <div className="hidden absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
              🖼️ Vista previa no disponible: {files[activePreview]?.name}
            </div>
          </div>

          {/* Navigation: prev/next + counter */}
          {files.length > 1 && (
            <div className="flex items-center justify-between px-4 py-2 bg-white border-t border-gray-200">
              <button
                type="button"
                onClick={() => setActivePreview(p => Math.max(0, p - 1))}
                disabled={activePreview === 0}
                className="text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-300 disabled:cursor-not-allowed font-medium"
              >
                ◄ Anterior
              </button>
              <span className="text-sm text-gray-500 font-medium">
                {activePreview + 1} / {files.length}
              </span>
              <button
                type="button"
                onClick={() => setActivePreview(p => Math.min(files.length - 1, p + 1))}
                disabled={activePreview === files.length - 1}
                className="text-sm text-purple-600 hover:text-purple-800 disabled:text-gray-300 disabled:cursor-not-allowed font-medium"
              >
                Siguiente ►
              </button>
            </div>
          )}

          {/* Thumbnail strip */}
          {files.length > 1 && (
            <div className="flex gap-2 px-4 py-3 bg-white border-t border-gray-100 overflow-x-auto">
              {files.map((f, i) => {
                const isImage = f.type.startsWith('image/');
                const isActive = i === activePreview;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActivePreview(i)}
                    className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all product-media ${
                      isActive ? 'border-purple-500 ring-2 ring-purple-200' : 'border-gray-200 opacity-70 hover:opacity-100'
                    }`}
                  >
                    {isImage ? (
                      <img
                        src={getPreviewUrl(f, i)}
                        alt=""
                        className="w-full h-full"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).parentElement!.innerText = '📷';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-lg">
                        🎬
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {status === 'loading' && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">
            {isEdit ? 'Guardando cambios...' : 'Subiendo artículo...'} <strong>{name}</strong>
          </p>
          {files.length > 0 && (
            <p className="text-sm text-gray-400 mt-2">{files.length} archivo(s)</p>
          )}
        </div>
      )}

      {status === 'success' && (
        <div className="text-center py-8 bg-green-50 rounded-xl border-2 border-green-300">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">{isEdit ? '¡Cambios guardados!' : '¡Artículo creado!'}</h2>
          {!isEdit && (
            <p className="text-gray-700 mb-1">
              Código: <strong className="text-lg">{resultCode}</strong>
            </p>
          )}
          <p className="text-gray-500 mb-6">{name}</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <button
              onClick={() => navigate('/admin/op/' + (isEdit ? editCode : resultCode))}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
            >
              👁️ Ver en consola
            </button>
            {!isEdit && (
              <button
                onClick={resetForm}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors"
              >
                📤 Subir otro artículo
              </button>
            )}
          </div>
        </div>
      )}

      {status === 'error' && (
        <div className="text-center py-8 bg-red-50 rounded-xl border-2 border-red-300 mb-6">
          <div className="text-5xl mb-4">❌</div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => { setStatus('idle'); setError(null); }}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors"
          >
            Intentar de nuevo
          </button>
        </div>
      )}

      {status === 'idle' && !editLoading && !editLoadError && (
        <form key={formKey} onSubmit={handleSubmit} className="space-y-5">
          {/* File input — SOLO Create Mode (UX-001E: la media se gestiona en la consola) */}
          {!isEdit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fotos / Videos <span className="text-gray-400 font-normal">(máx. {MAX_FILES} archivos)</span>
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors relative">
              <input
                id="upload-file-input"
                type="file"
                multiple
                accept={
                  mediaLimits
                    ? [...(mediaLimits.allowedImageTypes || []), ...(mediaLimits.allowedVideoTypes || [])].join(',')
                    : 'image/*,video/*'
                }
                onChange={handleFiles}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                style={{ fontSize: 0, zIndex: 10 }}
              />
              <label htmlFor="upload-file-input" className="cursor-pointer block relative z-0 pointer-events-none">
                <span className="text-purple-600 font-medium text-lg">
                  📁 {files.length === 0 ? 'Seleccionar archivos' : `${files.length} archivo(s) seleccionado(s)`}
                </span>
                <p className="text-xs text-gray-400 mt-1">
                  Fotos: {mediaLimits?.imageLabels || 'JPG, PNG, WebP'} | Videos: {mediaLimits?.videoLabels || 'MP4, MOV, WebM'}
                </p>
                <p className="text-xs text-gray-400">
                  Máx. 50MB por archivo
                </p>
              </label>
            </div>
            {/* File list */}
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm text-gray-600 bg-gray-50 px-3 py-1.5 rounded-lg">
                    <span className="truncate max-w-[70%]">
                      {f.type.startsWith('video/') ? '🎬' : '🖼️'} {f.name}
                    </span>
                    <span className="text-xs text-gray-400">
                      {(f.size / 1024 / 1024).toFixed(1)} MB
                    </span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-red-500 hover:text-red-700 ml-2 text-sm font-medium"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre del artículo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Blusa Floral Bordada"
              maxLength={200}
              required
            />
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Precio <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={price}
              onChange={e => setPrice(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: 1500"
              min="1"
              step="1"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <input
              type="text"
              value={category}
              onChange={e => setCategory(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Blusas"
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-colors"
              placeholder="Ej: Bordada a mano, talla M, color azul"
              maxLength={2000}
              rows={3}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
          >
            {isEdit ? '💾 Guardar cambios' : '📤 Subir artículo'}
          </button>
        </form>
      )}
    </div>
  );
};
